const MVCObject = require("../mvcobject").MVCObject;
const PlacesSearchSource = require("../location/placessearchsource").PlacesSearchSource;
const TemplateRenderer = require("../templaterenderer").TemplateRenderer;
const $ = require("../jquery_with_plugins");

/**
 * Constructs a new TableView
 * @constructor
 * @param {Object} templates should contain two templates accessible through keys "cell_store" and "cell_place". Store's template can also use deprecated key "cell"
 * @param {woosmap.location.PlacesSearchSource} [placesSearchSource] the placesSearchSource instance to use for getDetails.
 * @example TableView container
 * ```html
 * <div class="woosmap-tableview-container"></div>
 * ```
 *
 * @example TableView Store Cell template example
 * ```html
 * <div>{{name}}</div>
 * <a class="title">{{name}}</a><br/>
 * <small class="quiet">{{address.city}}</small>
 * <div>{{address.lines}} {{address.city}} {{address.zip}}</div>
 * <div>Tel : <a href="tel:{{phone}}">{{phone}}</a></div>
 * ```
 *
 * @example TableView Places Cell template example
 * ```html
 * <div>{{description}}</div>
 * ```
 *
 * If a card is selected its container will bear `woosmap-tableview-selected-cell` class.
 * If a card is highlighted using arrow keys, its container will bear `woosmap-tableview-highlighted-cell` class.
 *
 * @extends woosmap.utils.MVCObject
 * @memberOf woosmap.ui
 */
const TableView = function (templates, placesSearchSource) {
    /**
     * @type {woosmap.TemplateRenderer}
     * @private
     */
    this._store_card_renderer = new TemplateRenderer(templates.cell_store || templates.cell);

    /**
     * @type {woosmap.TemplateRenderer}
     * @private
     */
    this._places_card_renderer = new TemplateRenderer(templates.cell_place || "");

    this.highlighted_card_index = -1;

    /**
     * An array of stores. This property is bindable.
     * @see woosmap.utils.MVCObject#bindTo
     * @type {Array}
     */
    this.stores = null;

    /**
     * An array of Places predictions. This property is bindable.
     * @see woosmap.utils.MVCObject#bindTo
     * @type {Array}
     */
    this.predictions = null;

    /**
     * When user click on a Store cell, it sets this property
     * This property is observable
     * @type {Object}
     */
    this.selectedStore = null;

    /**
     * When user click on a Places cell, it sets this property
     * This property is observable
     * @type {Object}
     */
    this.location = null;

    /**
     * When user selects a places prediction it sets this property with places details
     * This property is observable
     * @type {Object}
     */
    this.details = null;

    this.tabIndex = -1;
    this._firstStoreCard = null;
    this._onCellCreatedCallback = null;
    // this.$cards = $('<div class="woosmap-tableview-container card_container">');
    this.$cards = $('<ul class="wm-autocomplete--list">');

    /**
     * The places search source used to get places details from the table view.
     * @type {PlacesSearchSource}
     * @private
     */
    this._placesSearchSource = placesSearchSource || new PlacesSearchSource();

    this.setupInteraction();
};

TableView.prototype = new MVCObject();

/**
 * Setups the interactions with the keyboard
 */
TableView.prototype.setupInteraction = function () {
    $(document).keyup((event) => {
        if (
            $(event.target).hasClass("woosmap-tableview-cell") ||
            (this.get("predictions") && this.get("predictions").length > 0)
        ) {
            const current_index = this.get("highlighted_card_index");

            if (event.keyCode === 13 && current_index > -1) {
                $(this.$cards.children()[current_index]).click();
            } else if (event.keyCode === 40 && current_index < this.$cards.children().length - 1) {
                this.set("highlighted_card_index", current_index + 1);
            } else if (event.keyCode === 38 && current_index >= 0) {
                this.set("highlighted_card_index", current_index - 1);
            }
        }
    });
};

/**
 * method is called when highlighted_card_index changes
 * @private
 */
TableView.prototype.highlighted_card_index_changed = function () {
    $(this.$cards.children()).removeClass("woosmap-tableview-highlighted-cell");
    const selectedCell = $(this.$cards.children()[this.get("highlighted_card_index")]);
    if (selectedCell.length > 0) {
        this.scrollToCell(selectedCell);
    }
    selectedCell.addClass("woosmap-tableview-highlighted-cell");
};

/**
 * When the selectedStore changes select the right cell in the table view.
 * If the table view cell is not visible then scroll the list to render it visible.
 */
TableView.prototype.selectedStore_changed = function () {
    const selectedStore = this.get("selectedStore");
    this.$cards
        .find(".selected_card, .woosmap-tableview-selected-cell")
        .removeClass("selected_card woosmap-tableview-selected-cell");
    this.$cards.find(".woosmap-tableview-highlighted-cell").removeClass("woosmap-tableview-highlighted-cell");

    if (selectedStore) {
        this.$cards
            .find(`#store_${selectedStore.properties.store_id}`)
            .addClass("selected_card woosmap-tableview-selected-cell");
        const selectedCell = this.$cards.find(".selected_card, .woosmap-tableview-selected-cell");
        if (selectedCell.length > 0) {
            this.scrollToCell(selectedCell);
            this.set("highlighted_card_index", this.$cards.children().index(selectedCell));
        }
    }
};

/**
 * Scrolls the table view to show the `$cell`
 * @param {jQuery}$cell
 */
TableView.prototype.scrollToCell = function ($cell) {
    const cellOffset = $cell.offset().top;
    if (
        cellOffset > this.$cards.offset().top + this.$cards.height() - $cell.height() ||
        cellOffset < this.$cards.offset().top
    ) {
        this.$cards.animate(
            {
                scrollTop: $cell.offset().top - this._firstStoreCard.offset().top,
            },
            500,
        );
    }
};

/**
 * When the stores list changes update the table view accordingly.
 */
TableView.prototype.stores_changed = function () {
    this.update();
};

/**
 * When the predictions list changes update the table view accordingly.
 */
TableView.prototype.predictions_changed = function () {
    this.update();
};

/**
 * Returns the container of the TableView.
 * @returns {*}
 */
TableView.prototype.getContainer = function () {
    return this.$cards[0];
};

/**
 * Displays the `stores` by rendering a cell for each store.
 */
TableView.prototype.update = function () {
    this.tabIndex = -1;
    this.set("highlighted_card_index", -1);
    this.$cards.html("");
    this._add_stores();
    this._add_places();
    this.$cards.scrollTop(0);
};

/**
 * Adds the predictions's cards to the list
 * @private
 */
TableView.prototype._add_places = function () {
    let predictions;
    let $rendered_card;
    let $store_container;
    predictions = this.get("predictions");

    if (predictions) {
        for (const prediction in predictions) {
            this.tabIndex++;
            if (predictions.hasOwnProperty(prediction)) {
                ((predictionData) => {
                    $store_container = $(`<div tabindex="${this.tabIndex}"></div>`)
                        .css({ outline: 0 })
                        .addClass("woosmap-tableview-cell");
                    $rendered_card = $(this._places_card_renderer.render(predictionData));
                    $store_container.append($rendered_card);
                    $store_container.click(() => {
                        this._placesSearchSource.getDetails(predictionData, (placeData) => {
                            this.set("predictions", []);
                            this.set("highlighted_card_index", -1);
                            this.set("location", { lat: placeData.location.lat, lng: placeData.location.lng });
                            this.set("details", placeData);
                        });
                    });
                    this.$cards.append($store_container);
                })(predictions[prediction]);
            }
        }
    }
};

/**
 * Adds the store's cards to the list
 * @private
 */
TableView.prototype._add_stores = function () {
    let stores;
    let $rendered_card;
    let $store_container;
    stores = this.get("stores");
    this._firstStoreCard = null;
    for (const store in stores) {
        if (stores.hasOwnProperty(store)) {
            ((storeData) => {
                $rendered_card = $(this._store_card_renderer.render(storeData.properties));
                $store_container = $(
                    `<div id="store_${storeData.properties.store_id}" tabindex="${this.tabIndex}"></div>`,
                )
                    .css({ outline: 0 })
                    .addClass("woosmap-tableview-cell");
                if (!this._firstStoreCard) {
                    this._firstStoreCard = $store_container;
                }
                $store_container.append($rendered_card);
                $store_container.click(() => {
                    this.set("selectedStore", storeData);
                });
                if (this._onCellCreatedCallback) {
                    this._onCellCreatedCallback($store_container, storeData);
                }
                this.$cards.append($store_container);
            })(stores[store]);
        }
    }
};

/**
 * Set new template for stores cells.
 * @param {string} storesTemplate new templates for stores cells.
 */
TableView.prototype.setStoresTemplate = function (storesTemplate) {
    this._store_card_renderer = new TemplateRenderer(storesTemplate);
};

/**
 * Set new template for places cells.
 * @param {string} placesTemplate new templates for places cells.
 */
TableView.prototype.setPlacesTemplate = function (placesTemplate) {
    this._places_card_renderer = new TemplateRenderer(placesTemplate);
};

/**
 * Set a function to be called by the tableView after the rendering of the cells.
 * @param {function} callback function call by the tableView. Params of the callback are `store_container` a jquery object which contain cell's html
 * and `storeData` a json containing the data of the store.
 */
TableView.prototype.setOnCellCreatedCallback = function (callback) {
    this._onCellCreatedCallback = callback;
};

exports.TableView = TableView;
